import type { WidgetConfigElement, InfoModalFormValues } from '~/types/widget';
import { INFO_MODAL_CATALOG, INFO_MODAL_BLOCK_ORDER, InfoModalBlockType } from './catalog/info-modal-catalog';

/**
 * Seeding + write-through for the info-modal visual builder.
 *
 * The builder is a click-to-edit authoring layer over the flat `InfoModalFormValues`: each block's
 * inspector controls are keyed by the form-value field they edit (e.g. `modalHeading`), so
 * write-through is a plain copy into the `infoModalFormValues` object the modal renders from.
 * Blocks are seeded in a fixed top-to-bottom order; nothing new is persisted beyond those fields.
 */

type InfoModalValues = Partial<InfoModalFormValues>;

/** Builds a block's inspector attributes by pulling each control's form-value field (when set). */
function seedAttributes(type: InfoModalBlockType, values: InfoModalValues): WidgetConfigElement['attributes'] {
  const attributes: WidgetConfigElement['attributes'] = {};

  for (const control of INFO_MODAL_CATALOG[type].controls) {
    const value = (values as Record<string, unknown>)[control.attr];
    if (value !== undefined && value !== null) attributes[control.attr] = value as string | number | boolean;
  }

  return attributes;
}

/**
 * Seeds the info-modal block stack from the form values, in the fixed render order.
 * Returns a clean config array (no editor ids) suitable for `store.setBuilderTree(...)`.
 */
export function seedBlocksFromInfoModal(values: InfoModalValues): WidgetConfigElement[] {
  return INFO_MODAL_BLOCK_ORDER.map(type => ({ attributes: seedAttributes(type, values), type }));
}

/**
 * Writes an inspector edit back to the flat `infoModalFormValues`. Each control's `attr` IS the
 * form-value field key, so this is a direct copy — the canvas and saved config both read these fields.
 */
export function syncBlockChangeToInfoModal(
  attr: string,
  value: string | number | boolean,
  values: Record<string, unknown>,
): void {
  values[attr] = value;
}
